/**
 * API Client pour la gestion des factures récurrentes
 * TASK-301: Factures Récurrentes - Sprint 3
 */
import { safeFetch, safeJsonParse, getAuthHeaders } from './api';
import type {
  RecurringInvoice,
  RecurringInvoiceStats,
  RecurringInvoiceForecast,
  RecurringInvoiceStatus,
  RecurringInvoicesResponse,
  CreateRecurringInvoiceDTO,
  UpdateRecurringInvoiceDTO,
} from '@/features/billing/types/billing.types';
import type { Invoice } from '@/features/billing/types/billing.types';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";


/**
 * API Recurring Invoices
 */
export const recurringInvoicesAPI = {
  /**
   * Récupérer toutes les factures récurrentes avec filtres optionnels
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    status?: RecurringInvoiceStatus;
  }): Promise<RecurringInvoicesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);

    const url = `${API_URL}/billing/recurring-invoices${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await safeFetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `Erreur lors de la récupération des factures récurrentes : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);

    // Format standard backend V2: {success: true, data: {...}}
    if (apiResponse.success && apiResponse.data) {
      const { templates = [], pagination } = apiResponse.data;
      return {
        data: templates,
        pagination: pagination || {
          total: templates.length || 0,
          page: 1,
          limit: templates.length || 0,
          pages: 1,
        },
      };
    }

    // Fallback
    return {
      data: Array.isArray(apiResponse.data) ? apiResponse.data : [],
      pagination: apiResponse.pagination || {
        total: 0,
        page: 1,
        limit: 20,
        pages: 1,
      },
    };
  },

  /**
   * Récupérer une facture récurrente par son ID
   */
  async getById(id: string): Promise<RecurringInvoice> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices/${id}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la récupération de la facture récurrente');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Créer une facture récurrente
   */
  async create(data: CreateRecurringInvoiceDTO): Promise<RecurringInvoice> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la création de la facture récurrente');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Mettre à jour une facture récurrente
   */
  async update(id: string, data: UpdateRecurringInvoiceDTO): Promise<RecurringInvoice> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la mise à jour de la facture récurrente');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Mettre en pause une facture récurrente
   */
  async pause(id: string): Promise<RecurringInvoice> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices/${id}/pause`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la mise en pause');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Réactiver une facture récurrente
   */
  async resume(id: string): Promise<RecurringInvoice> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices/${id}/resume`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la réactivation');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Annuler une facture récurrente
   */
  async cancel(id: string): Promise<RecurringInvoice> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices/${id}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de l\'annulation');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Générer manuellement une facture depuis le template
   */
  async generateInvoice(id: string): Promise<Invoice> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices/${id}/generate`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la génération de la facture');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Supprimer une facture récurrente
   */
  async delete(id: string): Promise<void> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la suppression');
    }
  },

  /**
   * Récupérer les statistiques des factures récurrentes
   */
  async getStats(): Promise<RecurringInvoiceStats> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices/stats`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la récupération des statistiques');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },

  /**
   * Récupérer la projection des revenus récurrents sur 3, 6 ou 12 mois
   */
  async getForecast(months: 3 | 6 | 12 = 6): Promise<RecurringInvoiceForecast> {
    const response = await safeFetch(`${API_URL}/billing/recurring-invoices/forecast?months=${months}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response).catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'Erreur lors de la récupération des projections');
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.success ? apiResponse.data : apiResponse;
  },
};
